import { useCallback } from 'react';

import { ProductType } from 'common/api/e-comm/models/Product';
import { CardReloadability, ReloadableCardInput } from 'common/domain/reloadable';
import { useRootSelector } from 'common/features/featuresReducer';
import { ReloadProductsById, selectReloadProducts } from 'common/features/store/duck/brands/duck';
import {
    EGiftReloadabilityOf,
    selectEGiftReloadability,
} from 'common/features/store/duck/wallet/selectors/reloadability';

const PHYSICAL_FILTER_TYPES: ReadonlySet<ProductType> = Object.freeze(
    new Set([ProductType.Reload])
);

// Physical cards keep the pre-policy rule (brand Reload product flag); eGifts defer to the
// domain policy and never read `card.isReloadable`.
export const getCardReloadability = (
    card: ReloadableCardInput,
    reloadProducts: ReloadProductsById,
    eGiftOf: EGiftReloadabilityOf
): CardReloadability => {
    if (card.type === ProductType.EGiftCard) {
        return eGiftOf(card);
    }

    return {
        isReloadable: Boolean(reloadProducts[card.brandId]?.isReloadable),
        filterTypes: PHYSICAL_FILTER_TYPES,
    };
};

/**
 * Wires both product maps from Redux and returns a stable resolver for a card's reloadability.
 */
export const useCardReloadability = () => {
    const reloadProducts = useRootSelector(selectReloadProducts);
    const eGiftOf = useRootSelector(selectEGiftReloadability);

    return useCallback(
        (card: ReloadableCardInput) => getCardReloadability(card, reloadProducts, eGiftOf),
        [reloadProducts, eGiftOf]
    );
};
